import { useState, type ReactNode } from 'react';
import { generateIdempotencyKey } from '@/api/client/http';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Select } from '@/shared/ui/select';
import { Textarea } from '@/shared/ui/textarea';
import { ATTRIBUTE_KIND_OPTIONS } from '@/modules/catalog/pages/setup-page/parts/options';
import { parseOptions } from '@/modules/catalog/pages/setup-page/parts/drafts';
import type { TaxonomyAttributeKind } from '@/api/schemas/catalog-taxonomy';
import type { ManualAttributeInput, ManualCategoryInput } from '@/modules/catalog/pages/setup-page/types';

/**
 * The MANUAL forms.
 *
 * ---------------------------------------------------------------------------
 * AI IS AN AID, NEVER A REQUIREMENT
 * ---------------------------------------------------------------------------
 * These two forms are the promise the availability notice makes: everything the
 * assistant can propose, a merchant can type. They write through the SAME
 * catalogue API the assistant's proposals are saved through, so a hand-made
 * category and an assisted one are the same record.
 *
 * The idempotency key changes when the identity of the record changes — the
 * name or the slug — and holds still otherwise, so clicking "Create" twice
 * with the same details is one category, not two.
 */
export function ManualCategoryForm({
  onCreate,
  isWorking,
}: {
  readonly onCreate: (input: ManualCategoryInput) => void;
  readonly isWorking: boolean;
}): ReactNode {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [idempotencyKey, setIdempotencyKey] = useState(() => generateIdempotencyKey());

  const editable = name.trim() !== '' && slug.trim() !== '';

  function submit(): void {
    if (!editable) return;
    onCreate({ name: name.trim(), slug: slug.trim(), description, idempotencyKey });
  }

  return (
    <section className="space-y-3 rounded-lg border border-border bg-surface-raised p-3">
      <h3 className="text-sm font-medium text-content">Add a category by hand</h3>
      <div className="grid gap-3 md:grid-cols-2">
        <Input
          label="Name"
          value={name}
          onChange={(event) => {
            setName(event.target.value);
            setIdempotencyKey(generateIdempotencyKey());
          }}
        />
        <Input
          label="Slug"
          hint="Lower kebab-case."
          value={slug}
          onChange={(event) => {
            setSlug(event.target.value);
            setIdempotencyKey(generateIdempotencyKey());
          }}
        />
      </div>
      <Textarea
        label="Description"
        value={description}
        onChange={(event) => setDescription(event.target.value)}
      />
      <Button variant="secondary" disabled={!editable} loading={isWorking} onClick={submit}>
        Create category
      </Button>
    </section>
  );
}

export function ManualAttributeForm({
  onCreate,
  isWorking,
}: {
  readonly onCreate: (input: ManualAttributeInput) => void;
  readonly isWorking: boolean;
}): ReactNode {
  const [name, setName] = useState('');
  const [key, setKey] = useState('');
  const [kind, setKind] = useState<TaxonomyAttributeKind>('text');
  const [optionsText, setOptionsText] = useState('');
  const [idempotencyKey, setIdempotencyKey] = useState(() => generateIdempotencyKey());

  const editable = name.trim() !== '' && key.trim() !== '';

  function submit(): void {
    if (!editable) return;
    onCreate({
      key: key.trim(),
      name: name.trim(),
      kind,
      options: parseOptions(optionsText),
      idempotencyKey,
    });
  }

  return (
    <section className="space-y-3 rounded-lg border border-border bg-surface-raised p-3">
      <h3 className="text-sm font-medium text-content">Add an attribute by hand</h3>
      <ManualAttributeFields
        name={name}
        keyValue={key}
        kind={kind}
        optionsText={optionsText}
        onName={setName}
        onKey={setKey}
        onKind={setKind}
        onOptions={setOptionsText}
        onEdited={() => setIdempotencyKey(generateIdempotencyKey())}
      />
      <Button variant="secondary" disabled={!editable} loading={isWorking} onClick={submit}>
        Create attribute
      </Button>
    </section>
  );
}

/** The fields of the manual attribute form. */
function ManualAttributeFields({
  name,
  keyValue,
  kind,
  optionsText,
  onName,
  onKey,
  onKind,
  onOptions,
  onEdited,
}: {
  readonly name: string;
  readonly keyValue: string;
  readonly kind: TaxonomyAttributeKind;
  readonly optionsText: string;
  readonly onName: (value: string) => void;
  readonly onKey: (value: string) => void;
  readonly onKind: (value: TaxonomyAttributeKind) => void;
  readonly onOptions: (value: string) => void;
  readonly onEdited: () => void;
}): ReactNode {
  return (
    <>
      <div className="grid gap-3 md:grid-cols-2">
        <Input
          label="Name"
          value={name}
          onChange={(event) => {
            onName(event.target.value);
            onEdited();
          }}
        />
        <Input
          label="Key"
          hint="Lower snake case."
          value={keyValue}
          onChange={(event) => {
            onKey(event.target.value);
            onEdited();
          }}
        />
      </div>
      <Select
        label="Kind"
        options={ATTRIBUTE_KIND_OPTIONS}
        value={kind}
        onChange={(event) => onKind(event.target.value as TaxonomyAttributeKind)}
      />
      <Input
        label="Options"
        hint="Comma separated. Required for a select kind, and refused for the others."
        value={optionsText}
        onChange={(event) => onOptions(event.target.value)}
      />
    </>
  );
}
